import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { ListUiStringsParams, UiStringFormValues } from './schema';

const ROOT_KEY = 'uiStrings';
const listKey = (params: ListUiStringsParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useUiStrings(params: ListUiStringsParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listUiStrings(params),
    placeholderData: (prev) => prev,
  });
}

export function useUiString(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getUiString(id as string),
    enabled: !!id,
  });
}

export function useUpdateUiString() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: UiStringFormValues }) =>
      api.updateUiString(id, values),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      toast.success('Saved — changes propagate to mobile within 60 seconds');
    },
    onError: (err) =>
      toast.error(describeError(err, 'Failed to update UI string')),
  });
}
